import { useCallback, useEffect, useState } from "react";
import { fetchAgentReviewDashboard } from "../../data/api/agentReviewApi";
import { AgentReviewDashboard } from "../../domain/entities/AgentReviewEntities";

/**
 * ZOUR-67 — the dashboard's numbers.
 *
 * All of them come from `GET /api/agent-review/dashboard`, which counts only
 * each bill's *latest* run. A bill re-checked five times must not weigh five
 * times as much as one checked once, and that decision lives in the SQL rather
 * than here so the API and any hand-written query agree.
 */
export const useAgentReviewDashboard = (initialDays = 30) => {
  const [days, setDays] = useState(initialDays);
  // Off by default, like the Queue's switch: the dashboard answers "how is the
  // desk doing", and a historical corpus is not the desk.
  const [includePosted, setIncludePosted] = useState(false);
  const [data, setData] = useState<AgentReviewDashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await fetchAgentReviewDashboard(days, includePosted));
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Could not load the dashboard",
      );
    } finally {
      setLoading(false);
    }
  }, [days, includePosted]);

  useEffect(() => {
    load();
  }, [load]);

  return { data, loading, error, days, setDays, includePosted, setIncludePosted, reload: load };
};
